import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import type { IqServiceInventoryItem } from '@/types/iq-service-inventory';

interface IqServiceInventoryArchiveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Relaxed to a structural Pick so the platform-overview caller can pass a minimal
  // `{ iqServiceKey }` (the overview row carries no full item) while the per-merchant
  // caller keeps passing a full IqServiceInventoryItem — both satisfy this. The dialog
  // reads only these two fields. (Spec 356 §5; merchantName render is wired in Phase 4.)
  item: (Pick<IqServiceInventoryItem, 'iqServiceKey'> & { displayName?: string }) | null;
  /**
   * Optional merchant name. The cross-merchant platform overview supplies it so the
   * confirm names WHICH merchant is affected (Spec 356 §5); the per-merchant caller omits
   * it (the merchant is unambiguous on that page).
   */
  merchantName?: string;
  onConfirm: () => void;
  isPending?: boolean;
}

/**
 * Soft-archive confirmation (UC-4). Archiving sets status=archived; it never breaks
 * running subscriptions (the FallbackChain string stays the TX source) and is
 * idempotent.
 */
export function IqServiceInventoryArchiveDialog({
  open,
  onOpenChange,
  item,
  merchantName,
  onConfirm,
  isPending,
}: IqServiceInventoryArchiveDialogProps) {
  const { t } = useTranslation('iq-service-inventory');
  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
              <Archive className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <DialogTitle>{t('archive.title')}</DialogTitle>
              <DialogDescription>{t('archive.warning')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2 text-sm">
          {merchantName && (
            <p className="mb-1 text-muted-foreground">
              {t('overview.crud.archiveMerchant', { merchant: merchantName })}
            </p>
          )}
          <p className="font-mono break-all">{item.iqServiceKey}</p>
          {item.displayName && <p className="mt-1 text-muted-foreground">{item.displayName}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            {t('archive.cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {t('archive.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
